import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuUploadForm from './MenuUploadForm';
import MenuUrlExtractor from './MenuUrlExtractor';
import MenuImageImport from './MenuImageImport';
import MenuItemDetail from './MenuItemDetail';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-hot-toast';
import { trackUpgradePromptShown, trackUpgradePromptClick } from '../utils/analytics';
import './UserDashboard.css';
import { useDebounce } from 'use-debounce';

const UserDashboard = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showImageImport, setShowImageImport] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [menuLimit, setMenuLimit] = useState({ used: 0, total: 3 });
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showUrlExtractor, setShowUrlExtractor] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, menuId: null, title: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => localStorage.getItem('user_onboarding_dismissed') === 'true');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchMenus();
    fetchSubscriptionStatus();
    fetchUsageStats();
    fetchFavorites();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/usage-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (err) {
      console.error('Error fetching usage stats:', err);
    }
  };

  const fetchFavorites = async () => {
    try {
      setFavoritesLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/favorites/mine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleRemoveFavorite = async (menuItemId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/menu/${menuItemId}/favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setFavorites(prev => prev.filter(f => f._id !== menuItemId));
        toast.success('Removed from favorites');
      }
    } catch (err) {
      toast.error('Failed to remove favorite');
    }
  };

  useEffect(() => {
    const filtered = menuItems.filter(item =>
      item.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    setFilteredMenuItems(filtered);
  }, [debouncedSearchTerm, menuItems]);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/menus`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch menus');
      
      const data = await response.json();
      setMenuItems(data.menus);
      setMenuLimit(data.menuLimit);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // If user has a Stripe customer ID but is still on free tier, try to sync
        // This recovers from webhook failures
        if (data.user?.tier === 'free' && data.user?.stripeCustomerId) {
          console.log('Detected possible sync issue, attempting to sync subscription...');
          await syncSubscription();
          // Refetch status after sync
          const syncedResponse = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (syncedResponse.ok) {
            const syncedData = await syncedResponse.json();
            setSubscriptionInfo(syncedData);
            if (syncedData.user?.limits?.maxMenus) {
              setMenuLimit(prev => ({ ...prev, total: syncedData.user.limits.maxMenus }));
            }
            return;
          }
        }
        
        setSubscriptionInfo(data);
        // Update menu limit based on subscription
        if (data.user?.limits?.maxMenus) {
          setMenuLimit(prev => ({ ...prev, total: data.user.limits.maxMenus }));
        }
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };
  
  const syncSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Subscription synced:', data);
        return data;
      }
    } catch (err) {
      console.error('Error syncing subscription:', err);
    }
  };

  const handleUploadSuccess = (newItem) => {
    // Update menu items and filtered menu items
    setMenuItems(prev => {
      // Check if this is an update to an existing menu item
      if (editingItem && editingItem._id) {
        // Replace the existing item with the updated one
        const updated = prev.map(item => 
          item._id === editingItem._id ? newItem : item
        );
        setFilteredMenuItems(updated);
        return updated;
      } else {
        // This is a new menu item, append it to the list
        const updated = [...prev, newItem];
        setFilteredMenuItems(updated);
        return updated;
      }
    });
    
    // Clear editing state
    setEditingItem(null);
    setShowUploadForm(false);
    
    // Set success message
    setSuccessMessage('Menu item saved successfully!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleExtractSuccess = (extractedData) => {
    // Format the data for the form
    const formattedData = {
      title: extractedData.title || '',
      description: extractedData.description || '',
      detailedDescription: extractedData.detailedDescription || extractedData.description || '',
      cuisine: extractedData.cuisine || '',
      ingredients: extractedData.ingredients || [],
      instructions: extractedData.instructions || [],
      nutrition: extractedData.nutrition || {},
      contentUrl: extractedData.contentUrl || extractedData.url || extractedData.sourceUrl || '',
      image: extractedData.image || extractedData.imageUrl || extractedData.mainImageUrl || '',
      servings: extractedData.servings || 1, // Ensure servings has a default value
      prepTime: extractedData.prepTime || '',
      cookTime: extractedData.cookTime || '',
      totalTime: extractedData.totalTime || ''
    };
    
    // Set the extracted data as the editing item
    setEditingItem(formattedData);
    
    // Show the upload form and hide the extractor
    setShowUploadForm(true);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    setSuccessMessage('Recipe extracted. Review the fields below before saving.');
    setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
    
    // Scroll after the form mounts so the transition is visible on tablets/mobile.
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleImageImportSuccess = (extractedData) => {
    // Normalize the extracted data for the form
    const normalizedData = {
      ...extractedData,
      contentUrl: extractedData.contentUrl || '',
      cuisine: extractedData.cuisine || '',
    };
    setEditingItem(normalizedData);
    setShowUploadForm(true);
    setShowImageImport(false);
    setShowUrlExtractor(false);
    
    // Refresh usage stats after extraction
    fetchUsageStats();
    
    window.scrollTo(0, 0);
    
    // Show confidence info if available
    if (extractedData._extractionConfidence) {
      const confidence = extractedData._extractionConfidence;
      const missingFields = confidence.missingFields || [];
      if (missingFields.length > 0) {
        toast(`Some fields couldn't be extracted: ${missingFields.join(', ')}. Please review and complete the form.`, {
          duration: 5000,
          icon: '⚠️'
        });
      }
    }
  };

  const handleMenuItemClick = (menuId) => {
    navigate(`/menu/${menuId}`, {
      state: { returnPath: location.pathname }
    });
  };

  const handleUpgradeClick = (source = 'dashboard') => {
    trackUpgradePromptClick(source);
    navigate('/pricing');
  };

  // Upgrade-funnel impressions — mirror the render conditions of the three
  // upgrade surfaces below so shown→click→purchase is measurable.
  const menuLimitFull =
    menuLimit.total !== 999 && menuLimit.used >= menuLimit.total;
  const menuLimitNudge =
    menuLimit.total !== 999 &&
    menuLimit.used >= Math.ceil(menuLimit.total * 0.7) &&
    menuLimit.used < menuLimit.total &&
    subscriptionInfo?.user?.tier === 'free';
  const trialBannerVisible = subscriptionInfo?.user?.tier === 'free';
  useEffect(() => {
    if (menuLimitFull) trackUpgradePromptShown('menu_limit');
  }, [menuLimitFull]);
  useEffect(() => {
    if (menuLimitNudge) trackUpgradePromptShown('menu_limit_nudge');
  }, [menuLimitNudge]);
  useEffect(() => {
    if (trialBannerVisible) trackUpgradePromptShown('trial_banner');
  }, [trialBannerVisible]);

  const getTierBadge = () => {
    const tier = subscriptionInfo?.user?.tier || 'free';
    const tierLabels = {
      free: { label: 'Free', className: 'tier-free' },
      standard: { label: 'Standard', className: 'tier-standard' },
      premium: { label: 'Premium', className: 'tier-premium' }
    };
    return tierLabels[tier] || tierLabels.free;
  };

  const handleDeleteClick = (menuId, title) => {
    setDeleteConfirm({ isOpen: true, menuId, title });
  };

  const handleDeleteConfirm = async () => {
    const { menuId } = deleteConfirm;
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/menus/${menuId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete menu');
      }

      setMenuItems(prev => prev.filter(item => item._id !== menuId));
      toast.success('Menu deleted successfully');
      setDeleteConfirm({ isOpen: false, menuId: null, title: '' });
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error('Failed to delete menu');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem('user_onboarding_dismissed', 'true');
  };

  const onboardingItems = [
    {
      id: 'menu',
      label: 'Create your first menu',
      hint: 'Add a recipe manually, extract from a URL, or import from an image.',
      done: menuLimit.used > 0,
      action: () => { setShowUploadForm(true); setShowUrlExtractor(false); setShowImageImport(false); },
      actionLabel: 'Add Menu'
    },
    {
      id: 'nutrition',
      label: 'Set a nutrition goal',
      hint: 'Define your daily calorie and macro targets to stay on track.',
      done: (usageStats?.usage?.mealPlanifyThisMonth || 0) > 0,
      action: () => navigate('/nutrition'),
      actionLabel: 'Set Goal'
    }
  ];
  const onboardingCompleted = onboardingItems.filter(i => i.done).length;
  const showOnboarding = subscriptionInfo?.user?.tier === 'free' && !onboardingDismissed && !loading && onboardingCompleted < onboardingItems.length;

  const EmptyState = () => (
    <div className="empty-state">
      <h2>Create Your First Personal Menu</h2>
      <p>Upload your favorite recipe and use our platform to easily order ingredients!</p>
      <div className="empty-state-actions">
        <button 
          className="add-menu-button"
          onClick={() => {
            setShowUploadForm(true);
            setShowUrlExtractor(false);
            setShowImageImport(false);
          }}
        >
          + Add Manually
        </button>
        <button 
          className="extract-menu-button"
          onClick={() => {
            setShowUrlExtractor(true);
            setShowUploadForm(false);
            setShowImageImport(false);
          }}
        >
          <i className="fas fa-link"></i> Extract from URL
        </button>
        <button 
          className="image-import-button"
          onClick={() => {
            setShowImageImport(true);
            setShowUploadForm(false);
            setShowUrlExtractor(false);
          }}
        >
          <i className="fas fa-camera"></i> Import from Image
        </button>
      </div>
    </div>
  );

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <div className="header-title-row">
          <h1>My Personal Menus</h1>
          <span className={`tier-badge ${getTierBadge().className}`}>
            {getTierBadge().label}
          </span>
        </div>
        <div className="menu-limit-indicator">
          <span className="menu-count">
            {menuLimit.used} of {menuLimit.total === 999 ? '∞' : menuLimit.total} menus used
          </span>
          {menuLimit.used >= menuLimit.total && menuLimit.total !== 999 && (
            <div className="upgrade-section">
              <button 
                className="upgrade-button"
                onClick={() => handleUpgradeClick('menu_limit')}
              >
                <span className="crown-icon">👑</span> Upgrade Plan
              </button>
            </div>
          )}
          {menuLimit.used >= Math.ceil(menuLimit.total * 0.7) && menuLimit.used < menuLimit.total && menuLimit.total !== 999 && subscriptionInfo?.user?.tier === 'free' && (
            <div className="upgrade-nudge">
              <span className="nudge-text">Almost full — upgrade for {subscriptionInfo?.user?.tier === 'free' ? '25' : 'more'} menus</span>
              <button onClick={() => handleUpgradeClick('menu_limit_nudge')} className="nudge-btn">Upgrade</button>
            </div>
          )}
        </div>
        {subscriptionInfo?.user?.tier === 'free' && (
          <div className="upgrade-banner">
            <i className="fas fa-rocket"></i>
            <span>Try Standard free for 7 days — 25 menus, 100 AI messages & more!</span>
            <button onClick={() => handleUpgradeClick('trial_banner')} className="upgrade-banner-btn">
              Start Free Trial
            </button>
          </div>
        )}
      </div>

      {showOnboarding && (
        <div className="user-onboarding-checklist">
          <div className="user-onboarding-header">
            <div>
              <h3>🚀 Get Started with FreshFitLab</h3>
              <p className="user-onboarding-progress-text">
                {onboardingCompleted} of {onboardingItems.length} steps complete
              </p>
            </div>
            <button className="user-onboarding-dismiss" onClick={handleDismissOnboarding} title="Dismiss">✕</button>
          </div>
          <div className="user-onboarding-progress-bar">
            <div
              className="user-onboarding-progress-fill"
              style={{ width: `${(onboardingCompleted / onboardingItems.length) * 100}%` }}
            />
          </div>
          <ul className="user-onboarding-items">
            {onboardingItems.map(item => (
              <li key={item.id} className={`user-onboarding-item ${item.done ? 'done' : ''}`}>
                <span className="user-onboarding-check">
                  {item.done ? '✓' : <span className="user-onboarding-circle" />}
                </span>
                <div className="user-onboarding-item-content">
                  <span className="user-onboarding-item-label">{item.label}</span>
                  {!item.done && <span className="user-onboarding-item-hint">{item.hint}</span>}
                </div>
                {!item.done && (
                  <button className="user-onboarding-item-action" onClick={item.action}>
                    {item.actionLabel}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {menuItems.length === 0 && !showUploadForm && !showUrlExtractor && !showImageImport ? (
        <EmptyState />
      ) : (
        <>
          <div className="dashboard-actions">
            {menuLimit.used < menuLimit.total && (
              <div className="action-buttons">
                <button 
                  className="add-menu-button"
                  onClick={() => {
                    setEditingItem(null);
                    setShowUploadForm(!showUploadForm);
                    setShowUrlExtractor(false);
                    setShowImageImport(false);
                  }}
                >
                  {showUploadForm ? 'Cancel' : '+ Add New Menu'}
                </button>
                <button 
                  className="extract-menu-button"
                  onClick={() => {
                    setShowUrlExtractor(!showUrlExtractor);
                    setShowUploadForm(false);
                    setShowImageImport(false);
                    setEditingItem(null);
                  }}
                >
                  {showUrlExtractor ? 'Cancel' : '+ Extract from URL'}
                </button>
                <button 
                  className="image-import-button"
                  onClick={() => {
                    setShowImageImport(!showImageImport);
                    setShowUrlExtractor(false);
                    setShowUploadForm(false);
                    setEditingItem(null);
                  }}
                >
                  <i className="fas fa-camera"></i> {showImageImport ? 'Cancel' : 'Import from Image'}
                </button>
              </div>
            )}
            <div className="user-menu-search-container">
              <input
                type="text"
                placeholder="Search your menus..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="user-menu-search-input"
              />
            </div>
          </div>

          {showUrlExtractor && (
            <MenuUrlExtractor onExtractSuccess={handleExtractSuccess} />
          )}

          {showImageImport && (
            <MenuImageImport 
              onExtractSuccess={handleImageImportSuccess}
              onCancel={() => setShowImageImport(false)}
              apiEndpoint="/user/extract-menu-from-images"
              imageGenEndpoint="/user/generate-menu-image"
            />
          )}

          {showUploadForm && (
            <MenuUploadForm
              onSuccess={handleUploadSuccess}
              editingItem={editingItem}
              isPersonal={true}
              initialData={editingItem}
            />
          )}

          <div className="menu-items-grid">
            {filteredMenuItems.map(item => (
              <div key={item._id} className="menu-item-card">
                <div className="personal-badge">Personal Menu</div>
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="item-image"
                  onClick={() => handleMenuItemClick(item._id)}
                  style={{ cursor: 'pointer' }}
                />
                <div className="item-details">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="item-actions">
                    <button
                      onClick={() => handleMenuItemClick(item._id)}
                      className="view-button"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setShowUploadForm(true);
                        window.scrollTo(0, 0);
                      }}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item._id, item.title)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Favorites Section */}
      <div className="favorites-section">
        <h2 className="favorites-heading">
          <i className="fas fa-star" style={{ color: '#f6ad55', marginRight: '8px' }}></i>
          Favorites
        </h2>
        {favoritesLoading ? (
          <p className="favorites-loading">Loading favorites...</p>
        ) : favorites.length === 0 ? (
          <p className="favorites-empty">
            No favorites yet. Browse the menu and click <strong>More &rarr; Add to Favorites</strong> on any recipe to save it here.
          </p>
        ) : (
          <div className="menu-items-grid">
            {favorites.map(item => (
              <div key={item._id} className="menu-item-card favorite-card">
                <div className="favorite-badge">
                  <i className="fas fa-star"></i> Favorite
                </div>
                <img
                  src={item.image}
                  alt={item.title}
                  className="item-image"
                  onClick={() => setSelectedFavorite(item)}
                  style={{ cursor: 'pointer' }}
                />
                <div className="item-details">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="item-actions">
                    <button
                      onClick={() => setSelectedFavorite(item)}
                      className="view-button"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleRemoveFavorite(item._id)}
                      className="delete-button"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedFavorite && (
        <MenuItemDetail
          item={selectedFavorite}
          onClose={() => {
            setSelectedFavorite(null);
            fetchFavorites();
          }}
        />
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onCancel={() => setDeleteConfirm({ isOpen: false, menuId: null, title: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Menu"
        message={`Are you sure you want to delete "${deleteConfirm.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={isDeleting}
      />
    </div>
  );
};

export default UserDashboard; 